import {Form} from 'antd';
import React, {FC} from 'react';

import {CustomDecimalInput} from 'components/form/components/custom-decimal-input';
import {CustomSelect} from 'components/form/inputs/custom-select';

import {useLookupCheck} from '../../hooks';
import {CHANGES_LOOKUP_URL, IS_NOT_CHANGED_LOOKUP_CODE, YES_NO_LOOKUP_URL} from '../../model/constants';

interface IAdditionalFieldsTab {
    isReadOnly?: boolean;
}

export const AdditionalFieldsTab: FC<IAdditionalFieldsTab> = ({
    isReadOnly = false,
}: IAdditionalFieldsTab) => {
    const isNotChanged = useLookupCheck(CHANGES_LOOKUP_URL, IS_NOT_CHANGED_LOOKUP_CODE);

    const createPercentField = (parentFieldName: string, percentFieldName: string, label: string) => (
        <Form.Item
            shouldUpdate={(prevValues, currentValues) => prevValues[parentFieldName] !== currentValues[parentFieldName]}
            noStyle
        >
            {({getFieldValue}) => {
                const parentValue = getFieldValue(parentFieldName);
                const isDisabled = isNotChanged(parentValue) || !parentValue;

                return (
                    <Form.Item
                        name={percentFieldName}
                        label={label}
                        dependencies={[parentFieldName, percentFieldName]}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                            ({getFieldValue: getFieldValueInValidator}) => ({
                                validator(_, value) {
                                    const currentParentValue = getFieldValueInValidator(parentFieldName);
                                    if (isNotChanged(currentParentValue)) {
                                        return Promise.resolve();
                                    }
                                    const isEmpty = value === ''
                                        || value === undefined
                                        || value === null;

                                    if (currentParentValue) {
                                        if (isEmpty) {
                                            return Promise.reject(new Error('Обязательное поле'));
                                        }

                                        const pattern = /^[+-]?\d{0,4}(?:\.\d{0,3})?$/;
                                        if (!pattern.test(value))
                                            return Promise.reject(new Error('Допустим ввод только цифр и запятой. ' +
                                                'Не более 4 цифр до запятой и 3 цифр после запятой.'));
                                    }

                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <CustomDecimalInput
                            placeholder="Введите значение"
                            autoComplete="off"
                            disabled={isDisabled || isReadOnly}
                        />
                    </Form.Item>
                );
            }}
        </Form.Item>
    );

    return (
        <div className="form-flex">
            <div className="form-flex-row">
                <Form.Item
                    name="suppliesDifficultId"
                    label="Имеются ли трудности с поставками сырья, компонентов, товаров?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: YES_NO_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="equipmentDifficultId"
                    label="Имеются ли трудности с поставками машин, оборудования и запчастей?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: YES_NO_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="maintenanceDifficultId"
                    label="Имеются ли трудности с обслуживанием машин и оборудования?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: YES_NO_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="priceId"
                    label="Цены на реализуемую продукцию (работы, услуги)"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'priceId',
                    'pricePercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="costImportId"
                    label="Стоимость импорта (машин, оборудования, сырья и компонентов)"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'costImportId',
                    'costImportPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="costLogisticId"
                    label="Затраты на логистику. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'costLogisticId',
                    'costLogisticPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="prodVolumeId"
                    label="Объемы производства (выполнения работ, оказания услуг). Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'prodVolumeId',
                    'prodVolumePercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="financCostId"
                    label="Стоимость расчетов. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'financCostId',
                    'financCostPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="staffId"
                    label="Количество сотрудников, занятых на производстве. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'staffId',
                    'staffPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="wageFundId"
                    label="Фонд оплаты труда. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'wageFundId',
                    'wageFundPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="homeRevenueId"
                    label="Выручка от реализации продукции (работ, услуг) на внутреннем рынке. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'homeRevenueId',
                    'homeRevenuePercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="foreignRevenueId"
                    label="Выручка от реализации продукции (работ, услуг) на внешнем рынке. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'foreignRevenueId',
                    'foreignRevenuePercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="creditorDebtId"
                    label="Размер кредиторской задолженности по отчетную дату. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'creditorDebtId',
                    'creditorDebtPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="debtorDebtId"
                    label="Размер дебиторской задолженности по отчетную дату. Как изменился показатель?"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: CHANGES_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
                {createPercentField(
                    'debtorDebtId',
                    'debtorDebtPercent',
                    'Оцените изменение показателя по отношению к значению за аналогичный период предыдущего года, %'
                )}
            </div>
        </div>
    );
};
